import InlineEditAutocomplete from '@components/v2/form/InlineEditAutocomplete/InlineEditAutocomplete';
import ObjectScreen from '@components/v2/layout/ObjectScreen/ObjectScreen';
import LangEnum from '../../../../../../../../../lang/LangEnum';
import { UpdateOrderGeneralDataParams } from '../../../../../../../../../services/modules/order/order.types';
import { getFormattedCodeName } from '../../../../../../../../../utils/formatters';
import { OrderObjectTestIds } from '../../../../../../../../../utils/testIds/OrderObject.testIds';
import { Consultant, Customer } from '../../../../../../../../customer/types/customer.types';
import OrderCustomerOptionRenderer from '../../../../../../../../order-book/OrderCustomerOptionRenderer';
import { PriceTable } from '../../../../../../../../price-tables/price-table.types';
import RachelChangedValue from '../../../../../../../../rachel/components/RachelChangedValue';
import { OrderError } from '../../../../../../../edit/sections/OrderEditSections.types';
import { Order } from '../../../../../../../order.types';
import { SalesRepresentativesAutocompleteItem } from '../../../../../../../types/salesRepresentativesAutocomplete.types';
import { OrderGeneralDataSources } from '../hooks/useOrderObjectGeneralDataSection';
import { getOrderGeneralDataFieldsWithError } from '../orderGeneralDataFieldErrors';
import {
  OrderGeneralDataFetchers,
  OrderSellerOption,
} from '../orderGeneralDataOfflineFetchers.types';
import { OrderGeneralDataPermissions } from '../orderGeneralDataPermissions';
import OrderCustomerReadOnlyFields from './OrderCustomerReadOnlyFields';

interface OrderGeneralDataFieldsProps {
  order: Order;
  canEditOrder: boolean;
  permissions: OrderGeneralDataPermissions;
  disabledFields: Record<keyof OrderGeneralDataSources, boolean>;
  isConsultantVisible: boolean;
  sources: OrderGeneralDataSources;
  fetchers?: Partial<OrderGeneralDataFetchers>;
  generalDataErrors?: OrderError[];
  submit: (data: UpdateOrderGeneralDataParams) => void;
}

const formatSalesRepresentativeOptions = (options: SalesRepresentativesAutocompleteItem[]) =>
  options.map((option) => ({
    ...option,
    id:
      (option as SalesRepresentativesAutocompleteItem & { salesRepresentativeId?: string })
        .salesRepresentativeId ?? option.id,
  }));

const formatSellerOptions = (options: OrderSellerOption[]) =>
  options.map((option) => ({
    ...option,
    id: option.userId ?? option.id,
  }));

const formatPriceTableOptions = (options: PriceTable[]) =>
  options.map((option) => ({
    ...option,
    id: (option as PriceTable & { priceTableId?: string }).priceTableId ?? option.id,
  }));

const OrderGeneralDataFields = ({
  order,
  canEditOrder,
  permissions,
  disabledFields,
  isConsultantVisible,
  sources,
  fetchers,
  generalDataErrors,
  submit,
}: OrderGeneralDataFieldsProps) => {
  const fieldsWithError = getOrderGeneralDataFieldsWithError(generalDataErrors);

  return (
    <>
      {canEditOrder ? (
        <ObjectScreen.Field
          label={LangEnum.GLOBAL_STORE}
          hasError={fieldsWithError.has('customer')}
          control={
            <InlineEditAutocomplete<Customer>
              value={order.customer?.id ?? null}
              displayLabel={order.customer?.commercialName}
              source={sources.customer}
              fetcher={fetchers?.customer}
              getOptionLabel={(option) => option.commercialName}
              renderOption={(option) => <OrderCustomerOptionRenderer customer={option} />}
              initialData={order.customer ? [order.customer] : []}
              isDisabled={disabledFields.customer}
              disableClearable={!permissions.canClearCustomer}
              hasError={fieldsWithError.has('customer')}
              testId={OrderObjectTestIds.GENERAL_DATA_CUSTOMER}
              onConfirm={(customerId) => submit({ customerId })}
            />
          }
        />
      ) : (
        <OrderCustomerReadOnlyFields customer={order.customer} />
      )}

      <ObjectScreen.Field
        label={LangEnum.GLOBAL_SELLER}
        control={
          <InlineEditAutocomplete<OrderSellerOption>
            value={order.seller?.id ?? null}
            displayLabel={order.seller?.name}
            source={sources.seller}
            fetcher={fetchers?.seller}
            getOptionLabel={(option) => option.name}
            formatOptions={formatSellerOptions}
            initialData={order.seller ? [order.seller] : []}
            isDisabled={disabledFields.seller}
            testId={OrderObjectTestIds.GENERAL_DATA_SELLER}
            onConfirm={(sellerId) => submit({ sellerId })}
          />
        }
      />

      <ObjectScreen.Field
        label={LangEnum.GLOBAL_SALES_REPRESENTATIVE}
        hasError={fieldsWithError.has('salesRepresentative')}
        control={
          <RachelChangedValue appearance="field" value={order.salesRepresentative?.id}>
            <InlineEditAutocomplete<SalesRepresentativesAutocompleteItem>
              value={order.salesRepresentative?.id ?? null}
              displayLabel={order.salesRepresentative?.name}
              source={sources.salesRepresentative}
              fetcher={fetchers?.salesRepresentative}
              getOptionLabel={(option) => option.name}
              formatOptions={formatSalesRepresentativeOptions}
              initialData={order.salesRepresentative ? [order.salesRepresentative] : []}
              isDisabled={disabledFields.salesRepresentative}
              hasError={fieldsWithError.has('salesRepresentative')}
              testId={OrderObjectTestIds.GENERAL_DATA_SALES_REPRESENTATIVE}
              onConfirm={(salesRepresentativeId) => submit({ salesRepresentativeId })}
            />
          </RachelChangedValue>
        }
      />

      {permissions.isPriceTableVisible && (
        <ObjectScreen.Field
          label={LangEnum.GLOBAL_PRICE_TABLE}
          hasError={fieldsWithError.has('priceTable')}
          control={
            <RachelChangedValue appearance="field" value={order.priceTable?.id}>
              <InlineEditAutocomplete<PriceTable>
                value={order.priceTable?.id ?? null}
                displayLabel={getFormattedCodeName(order.priceTable?.name, order.priceTable?.code)}
                source={sources.priceTable}
                fetcher={fetchers?.priceTable}
                getOptionLabel={(option) => getFormattedCodeName(option.name, option.code)}
                formatOptions={formatPriceTableOptions}
                initialData={order.priceTable ? [order.priceTable] : []}
                isDisabled={disabledFields.priceTable}
                hasError={fieldsWithError.has('priceTable')}
                testId={OrderObjectTestIds.GENERAL_DATA_PRICE_TABLE}
                onConfirm={(priceTableId) => submit({ priceTableId })}
              />
            </RachelChangedValue>
          }
        />
      )}

      {isConsultantVisible && (
        <ObjectScreen.Field
          label={LangEnum.GLOBAL_CONSULTANT}
          hasError={fieldsWithError.has('consultant')}
          control={
            <InlineEditAutocomplete<Consultant>
              value={order.consultant?.id ?? null}
              displayLabel={order.consultant?.name}
              source={sources.consultant}
              getOptionLabel={(option) => option.name}
              initialData={order.consultant ? [order.consultant] : []}
              isDisabled={disabledFields.consultant}
              hasError={fieldsWithError.has('consultant')}
              testId={OrderObjectTestIds.GENERAL_DATA_CONSULTANT}
              onConfirm={(consultantId) => submit({ consultantId })}
            />
          }
        />
      )}
    </>
  );
};

export default OrderGeneralDataFields;
